import { cva } from 'class-variance-authority';
import type { PasswordStengthIndicatorTranslations } from './types/translations';
import './password-strength-indicator.css';

type PasswordStrengthValue = 0 | 1 | 2 | 3 | 4;
type PasswordStrengthTone = 'empty' | 'weak' | 'medium' | 'good' | 'strong';

type PasswordStrength = {
   strength: PasswordStrengthValue;
   label: string;
   tone: PasswordStrengthTone;
};

const segmentClassName = cva('password-strength-indicator__segment', {
   variants: {
      tone: {
         empty: '',
         weak: 'password-strength-indicator__segment--weak',
         medium: 'password-strength-indicator__segment--medium',
         good: 'password-strength-indicator__segment--good',
         strong: 'password-strength-indicator__segment--strong',
      },
   },
   defaultVariants: {
      tone: 'empty',
   },
});

const labelClassName = cva('password-strength-indicator__label', {
   variants: {
      tone: {
         empty: '',
         weak: 'password-strength-indicator__label--weak',
         medium: 'password-strength-indicator__label--medium',
         good: 'password-strength-indicator__label--good',
         strong: 'password-strength-indicator__label--strong',
      },
   },
   defaultVariants: {
      tone: 'empty',
   },
});

export function PasswordStrengthIndicator({
   password,
   translations,
}: {
   password: string;
   translations: PasswordStengthIndicatorTranslations;
}) {
   const getPasswordStrength = (): PasswordStrength => {
      if (!password) {
         return { strength: 0, label: '', tone: 'empty' };
      }

      let strength = 0;
      if (password.length >= 8) strength++;
      if (/[A-Z]/.test(password)) strength++;
      if (/[a-z]/.test(password)) strength++;
      if (/[0-9]/.test(password)) strength++;
      if (/[^A-Za-z0-9]/.test(password)) strength++;

      if (strength <= 1)
         return {
            strength: 1,
            label: translations.password_strength_1,
            tone: 'weak',
         };
      if (strength <= 2)
         return {
            strength: 2,
            label: translations.password_strength_2,
            tone: 'medium',
         };
      if (strength <= 3)
         return {
            strength: 3,
            label: translations.password_strength_3,
            tone: 'good',
         };
      return {
         strength: 4,
         label: translations.password_strength_4,
         tone: 'strong',
      };
   };

   const translateMap: { [K in PasswordStrengthTone]: string } = {
      empty: '',
      weak: 'Слабкий',
      medium: 'Середній',
      good: 'Добрий',
      strong: 'Надійний',
   } as const;

   const passwordStrength = getPasswordStrength();

   return (
      <div className="password-strength-indicator">
         <div className="password-strength-indicator__segments">
            {[1, 2, 3, 4].map((level) => (
               <div
                  key={level}
                  className={segmentClassName({
                     tone: level <= passwordStrength.strength ? passwordStrength.tone : 'empty',
                  })}
               />
            ))}
         </div>
         <p className="password-strength-indicator__text">
            {'Надійність: '}
            <span className={labelClassName({ tone: passwordStrength.tone })}>
               {translateMap[passwordStrength.tone]}
            </span>
         </p>
      </div>
   );
}
